import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * ContractJobSelector Component
 *
 * Displays contract job variations as selectable cards with quantity inputs.
 * Calculates pricing for selected variations in real-time.
 *
 * Props:
 * - jobTypeId: ID of the contract job type
 * - onPriceCalculated: Callback when price is calculated (price, breakdown)
 * - onSelectionChange: Callback when selection changes (selections)
 */
const ContractJobSelector = ({
  jobTypeId,
  onPriceCalculated = () => {},
  onSelectionChange = () => {}
}) => {
  const [variations, setVariations] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [calculating, setCalculating] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState(null);

  // Fetch variations and config on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch variations
        const variationsResponse = await axios.get(
          `/api/contract-jobs/variations/${jobTypeId}`
        );
        setVariations(variationsResponse.data);

        // Fetch config
        const configResponse = await axios.get(
          `/api/contract-jobs/config/${jobTypeId}`
        );
        setConfig(configResponse.data);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching contract job data:', err);
        setError('Failed to load contract job data');
        setLoading(false);
      }
    };

    if (jobTypeId) {
      fetchData();
    }
  }, [jobTypeId]);

  // Calculate price whenever selections change
  useEffect(() => {
    const calculatePrice = async () => {
      // Get only variations with quantity > 0
      const selected = Object.entries(selectedVariations)
        .filter(([_, qty]) => qty > 0)
        .map(([variationId, quantity]) => ({
          variation_id: variationId,
          quantity: parseInt(quantity)
        }));

      if (selected.length === 0) {
        setPriceBreakdown(null);
        onPriceCalculated(0, null, config);
        onSelectionChange([]);
        return;
      }

      try {
        setCalculating(true);

        const response = await axios.post('/api/contract-jobs/calculate', {
          job_type_id: jobTypeId,
          selected_variations: selected
        });

        if (response.data.success) {
          setPriceBreakdown(response.data.breakdown);
          onPriceCalculated(response.data.total_price, response.data.breakdown, config);
          onSelectionChange(selected);
        }

        setCalculating(false);
      } catch (err) {
        console.error('Error calculating contract job price:', err);
        setCalculating(false);
      }
    };

    // Debounce calculation
    const timer = setTimeout(() => {
      calculatePrice();
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedVariations, jobTypeId]); // Removed callback dependencies to prevent infinite loop

  const handleQuantityChange = (variationId, quantity) => {
    const qty = parseInt(quantity) || 0;
    setSelectedVariations(prev => ({
      ...prev,
      [variationId]: qty
    }));
  };

  const handleToggleVariation = (variationId) => {
    setSelectedVariations(prev => {
      const current = prev[variationId] || 0;
      return {
        ...prev,
        [variationId]: current > 0 ? 0 : 1
      };
    });
  };

  if (loading) {
    return (
      <div className="p-5 bg-gray-50 rounded-lg">
        <div className="p-5 rounded-lg text-center text-base bg-blue-50 text-blue-700">
          Loading contract job variations...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 bg-gray-50 rounded-lg">
        <div className="p-5 rounded-lg text-center text-base bg-red-50 text-red-800">
          {error}
        </div>
      </div>
    );
  }

  if (!variations || variations.length === 0) {
    return (
      <div className="p-5 bg-gray-50 rounded-lg">
        <div className="p-5 rounded-lg text-center text-base bg-orange-50 text-orange-800">
          No variations available for this contract job.
        </div>
      </div>
    );
  }

  const totalSelected = Object.values(selectedVariations).reduce((sum, qty) => sum + qty, 0);

  return (
    <div className="p-5 bg-white border-4 border-blue-500 rounded-lg">
      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-4">
          {totalSelected > 0 && (
            <span className="text-sm text-gray-600 bg-blue-50 px-3 py-1.5 rounded border border-blue-300">
              {totalSelected} {totalSelected === 1 ? 'unit' : 'units'} selected
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
        {variations.map(variation => {
          const isSelected = (selectedVariations[variation.id] || 0) > 0;
          const quantity = selectedVariations[variation.id] || 0;

          return (
            <div
              key={variation.id}
              className={`
                border-2 rounded-md p-2.5 cursor-pointer
                flex flex-col items-stretch overflow-hidden
                transition-all duration-[400ms] ease-in-out
                hover:border-blue-500 hover:shadow-md hover:-translate-y-px
                ${isSelected
                  ? 'border-blue-500 bg-blue-500 shadow-lg'
                  : 'border-gray-300 bg-white'
                }
              `}
              onClick={() => handleToggleVariation(variation.id)}
            >
              <div className="mb-2">
                <div className={`font-bold text-sm leading-tight mb-1.5 ${
                  isSelected ? 'text-white' : 'text-gray-800'
                }`}>
                  {variation.variation_name}
                </div>
              </div>

              <div className={`my-1.5 text-xs leading-relaxed ${
                isSelected ? 'text-blue-100' : 'text-gray-600'
              }`}>
                <span className="inline">{variation.page_count}pp</span>
                <span className={`inline ${isSelected ? 'text-blue-200' : 'text-gray-400'}`}> • </span>
                <span className="inline">
                  {variation.finished_width_mm}×{variation.finished_height_mm}mm
                </span>
                <span className={`inline ${isSelected ? 'text-blue-200' : 'text-gray-400'}`}> • </span>
                <span className="inline">{variation.inner_stock_name}</span>
              </div>

              <div className="flex-grow" />

              <div className={`
                mt-2 pt-2 border-t border-blue-300
                flex items-center gap-1.5
                ${!isSelected ? 'hidden' : ''}
              `}>
                <label
                  htmlFor={`qty-${variation.id}`}
                  className="font-semibold text-white text-xs whitespace-nowrap"
                >
                  Qty:
                </label>
                <input
                  id={`qty-${variation.id}`}
                  type="number"
                  min="0"
                  value={quantity}
                  onChange={(e) => {
                    e.stopPropagation(); // Prevent card click when typing
                    handleQuantityChange(variation.id, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()} // Prevent card click when focusing input
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm min-w-[50px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white text-gray-800"
                />
              </div>

              <div className={`
                mt-1.5 pt-1.5 border-t border-blue-300 min-h-[50px]
                ${!isSelected ? 'hidden' : ''}
              `}>
                {priceBreakdown && priceBreakdown.variations
                  .filter(v => v.variation_id === variation.id)
                  .map(v => (
                    <div key={v.variation_id} className="flex flex-col gap-0.5">
                      <span className="text-base font-bold text-yellow-100">
                        £{v.total_price.toFixed(2)}
                      </span>
                      <span className="text-[0.7rem] text-blue-100">
                        £{v.price_per_unit.toFixed(2)}/unit
                      </span>
                    </div>
                  ))
                }
                {!priceBreakdown && (
                  <div className="flex flex-col gap-0.5 opacity-0 pointer-events-none">
                    <span className="text-base font-bold">&nbsp;</span>
                    <span className="text-[0.7rem]">&nbsp;</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {priceBreakdown && totalSelected > 0 && (
        <div className="bg-white border-2 border-green-600 rounded-lg p-5 mt-5">
          <div className="text-xl font-bold text-gray-800 mb-4 pb-2.5 border-b border-gray-300">
            Order Summary
          </div>
          <div className="flex flex-col gap-0">
            {/* Itemized variations */}
            <div className="divide-y divide-gray-200">
              {priceBreakdown.variations.map(variationBreakdown => {
                const variationDetail = variations.find(v => v.id === variationBreakdown.variation_id);
                return (
                  <div key={variationBreakdown.variation_id} className="py-3 flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800 mb-1">
                        {variationDetail?.variation_name || 'Unknown Variation'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {variationBreakdown.quantity} × £{variationBreakdown.price_per_unit.toFixed(2)}/unit
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <span className="font-semibold text-gray-800">
                        £{variationBreakdown.total_price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total */}
            <div className="flex justify-between text-xl mt-4 pt-4 border-t-2 border-gray-300">
              <span className="text-gray-600 font-medium">Total Price:</span>
              <span className="text-2xl text-green-600 font-bold">
                £{priceBreakdown.grand_total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractJobSelector;
